import {
  createBrowserRouter,
  Navigate,
  useRouteError,
} from 'react-router-dom';
import { lazy, Suspense } from 'react';

function RouteErrorPage() {
  const error = useRouteError() as { message?: string; stack?: string; status?: number; statusText?: string } | null;
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-xl w-full bg-white rounded-xl border border-red-200 shadow p-6 space-y-4">
        <h1 className="text-lg font-bold text-red-700">Application Error</h1>
        <p className="text-sm text-gray-700 font-medium">{error?.message ?? error?.statusText ?? 'Unknown error'}</p>
        {error?.stack && (
          <pre className="text-xs bg-gray-100 rounded p-3 overflow-x-auto text-gray-600 whitespace-pre-wrap">
            {error.stack}
          </pre>
        )}
        <button
          onClick={() => window.location.replace('/dashboard')}
          className="text-sm text-blue-600 hover:underline"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

import { ProtectedRoute } from '../components/shared/ProtectedRoute';
import { AppLayout } from '../components/layout/AppLayout';

// ── Eager loaded ──────────────────────────────────────────────
import LoginPage from '../pages/Auth/LoginPage';

// ── Lazy loaded pages ─────────────────────────────────────────
const DashboardPage      = lazy(() => import('../pages/Dashboard'));
const StockPage          = lazy(() => import('../pages/Stock'));
const SalesPage          = lazy(() => import('../pages/Sales'));
const CustomersPage      = lazy(() => import('../pages/Customers'));
const CreditsPage        = lazy(() => import('../pages/Credits'));
const ExpensesPage       = lazy(() => import('../pages/Expenses'));
const EODPage            = lazy(() => import('../pages/EOD'));
const InvoicesPage       = lazy(() => import('../pages/Invoices'));
const SettingsPage       = lazy(() => import('../pages/Settings'));
const ReceivablesPage    = lazy(() => import('../pages/Receivables'));
const CashInBankPage     = lazy(() => import('../pages/CashInBank'));
const CustomerDetailPage = lazy(() => import('../pages/Customers/detail'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  // Public routes
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorPage />,
  },

  // Protected routes wrapped in AppLayout
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute requiredPermission="dashboard:view">
            {withSuspense(<DashboardPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'stock',
        element: (
          <ProtectedRoute requiredPermission="stock:view">
            {withSuspense(<StockPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'sales',
        element: (
          <ProtectedRoute requiredPermission="sales:view">
            {withSuspense(<SalesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'customers',
        element: (
          <ProtectedRoute requiredPermission="customers:view">
            {withSuspense(<CustomersPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'credits',
        element: (
          <ProtectedRoute requiredPermission="credits:view">
            {withSuspense(<CreditsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'expenses',
        element: (
          <ProtectedRoute requiredPermission="expenses:view">
            {withSuspense(<ExpensesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'eod',
        element: (
          <ProtectedRoute requiredPermission="eod:view">
            {withSuspense(<EODPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'invoices',
        element: (
          <ProtectedRoute requiredPermission="invoices:view">
            {withSuspense(<InvoicesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'receivables',
        element: (
          <ProtectedRoute requiredPermission="credits:view">
            {withSuspense(<ReceivablesPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'cash-in-bank',
        element: (
          <ProtectedRoute superAdminOnly>
            {withSuspense(<CashInBankPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'customers/:id',
        element: (
          <ProtectedRoute requiredPermission="customers:view">
            {withSuspense(<CustomerDetailPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <ProtectedRoute superAdminOnly>
            {withSuspense(<SettingsPage />)}
          </ProtectedRoute>
        ),
      },
    ],
  },

  // Catch-all
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]);
